import React from 'react';
import style from './style.module.scss';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { Tag } from '../../components/tag';
import { ColumnProps } from 'antd/es/table';
import { AugmentedCategory, CategorySearchParams } from '../../stores/category';
import { useAuthStore, useCategoryStore } from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { getItemProp } from '../../utils';
import { PaginationBar } from '../../components/pagination/pagination-bar';

interface OnPromoClick {
    (id: number): (event: React.MouseEvent<HTMLDivElement>) => void;
}

const renderTitle = (_: unknown, record: AugmentedCategory) => {
    const enTitle = getItemProp(record.items, 'en', 'title');
    const frTitle = getItemProp(record.items, 'fr', 'title');
    return (
        <div>
            {enTitle}
            <br />
            <span className="light_label">{frTitle}</span>
        </div>
    );
};

const renderActivePromoCount = (_: unknown, record: AugmentedCategory) => {
    return <Tag variant="success">{record.activePromotionsCount}</Tag>;
};

const renderAssociatedPromotions = (onPromoClick: OnPromoClick) => {
    const AssociatedPromotionsComponent = (
        _: unknown,
        record: AugmentedCategory
    ) => {
        return (
            <div className={style.associated_promotions}>
                <div className={style.container}>
                    {record.promotions.map((promo) => (
                        <div
                            key={promo.id}
                            className={style.promo_container}
                            onClick={onPromoClick(promo.id)}
                        >
                            <img
                                className={style.promo_img}
                                src={promo.items[0].thumbnail ?? ''}
                                alt=""
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    AssociatedPromotionsComponent.displayName = 'AssociatedPromotionsComponent';
    return AssociatedPromotionsComponent;
};

export const CategoriesPage = observer(() => {
    const categoryStore = useCategoryStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const search = useSearchParams<CategorySearchParams>();

    React.useEffect(() => {
        categoryStore.fetchCategories(search.params);
    }, [categoryStore, search.params]);

    const onPromoClick: OnPromoClick = (promotionId) => (event) => {
        event.stopPropagation();
        navigation.goToCouponEdit(promotionId);
    };

    const columns: ColumnProps<AugmentedCategory>[] = [
        {
            title: 'Title',
            key: 'title',
            width: '20%',
            render: renderTitle,
        },
        {
            title: 'Active promo count',
            key: 'active_promo_count',
            render: renderActivePromoCount,
            width: '20%',
        },
        {
            title: 'Associated promotions',
            key: 'associated_promotions',
            render: renderAssociatedPromotions(onPromoClick),
            width: '70%',
        },
    ];

    const goToEditPage = (c: AugmentedCategory) => () => {
        navigation.goToCategoryEdit(c.id);
    };

    return (
        <>
            <PageTopBar title="Categories">
                <CreateButton onClick={() => navigation.goToCategoryCreate()} />
            </PageTopBar>

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <SearchInput
                        searchValue={search.params.q}
                        onChange={(e) =>
                            search.setParams({ q: e.target.value, page: 0 })
                        }
                        allowClear={true}
                        placeholder="Search"
                    />
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: categoryStore.categories?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<AugmentedCategory>
                className="clickable_table_body_rows"
                rowKey="id"
                dataSource={categoryStore.categories?.results}
                columns={columns}
                loading={categoryStore.isFetchingCount > 0}
                pagination={false}
                onRow={(r) => ({
                    onClick: goToEditPage(r),
                })}
            />
        </>
    );
});
